import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { PILLAR_INFO, getRandomQuote } from '@/lib/seeds';
import { PILLAR_SLUGS } from '@/lib/constants';
import { getTodayInTimezone, formatDateFriendly, DEFAULT_TIMEZONE } from '@/lib/timezone';
import { Quote, Flame, ChevronRight, TrendingUp, Plus } from 'lucide-react';
import * as Icons from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { TodayProgressSheet } from '@/components/TodayProgressSheet';
import { PillarRadarChart } from '@/components/charts/PillarRadarChart';
import { MicroCoach } from '@/components/coaching/MicroCoach';
import { StimulusCard } from '@/components/stimulus/StimulusCard';
import { IdentityVoteDisplay } from '@/components/identity/IdentityVoteDisplay';
import { useWeeklyScoreSync } from '@/hooks/useWeeklyScoreSync';
import { useBadgeCheckerOptimized } from '@/hooks/useBadgeCheckerOptimized';
import { useOnboardingGuard } from '@/hooks/useOnboardingGuard';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';
import { performanceService } from '@/services/performanceService';
import type { PillarSlug } from '@/types';
import { GroupsHomeSection } from '@/components/home/GroupsHomeSection';
import { useLazyLoad } from '@/hooks/useLazyLoad';
import { ChallengeSkeleton, PillarsSkeleton } from '@/components/skeletons/HomeSkeleton';

export default function Home() {
  useWeeklyScoreSync();
  useBadgeCheckerOptimized();
  
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { habits, challenges, goals } = useAppStore();
  const [progressSheetOpen, setProgressSheetOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [historicalScores, setHistoricalScores] = useState<Array<{
    weekStart: string;
    pillar: string;
    score: number;
  }>>([]);

  const challengeSection = useLazyLoad(0.1, '100px');
  const pillarsSection = useLazyLoad(0.1, '100px');

  // Controla exibição do tour apenas na primeira visita após onboarding
  useEffect(() => {
    if (!user) return;
    
    const hasSeenTour = localStorage.getItem('has_seen_tour');
    const onboardingJustCompleted = localStorage.getItem('onboarding_just_completed');
    
    // Mostra tour apenas se:
    // 1. Nunca viu o tour antes
    // 2. E acabou de completar o onboarding (flag temporária)
    if (!hasSeenTour && onboardingJustCompleted === 'true') {
      setShowTour(true);
      // Limpa a flag temporária
      localStorage.removeItem('onboarding_just_completed');
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }
    if (user && user.onboarding_completed === false) {
      navigate('/onboarding/intro');
    }
  }, [isAuthenticated, user, navigate]);

  const loadScores = useCallback(async () => {
    if (!user || user.pilares_foco.length === 0) return;
    
    try {
      await performanceService.calculateAndSaveWeeklyScores(user.id, habits, goals);
      const scores = await performanceService.getWeeklyPillarScores(user.id, 8);
      setHistoricalScores(scores);
    } catch (error) {
      // Silenciar erro - não é crítico
    }
  }, [user?.id, user?.pilares_foco.length, habits.length, goals.length]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);
  
  const userTimezone = user?.timezone || DEFAULT_TIMEZONE;
  const today = getTodayInTimezone(userTimezone);
  const dailyQuote = useMemo(() => getRandomQuote(), []);

  // ✅ Filtrar hábitos do usuário atual que estão ativos
  const todayHabits = habits?.filter(h => h.userId === user?.id && h.active) || [];
  const completedToday = todayHabits.filter(h => h.history?.includes(today) ?? false);
  const progressPercent = todayHabits.length > 0 ? Math.round(completedToday.length / todayHabits.length * 100) : 0;

  // ✅ Agrupar hábitos por pilar (suportando múltiplos pilares)
  const habitsByPillar = useMemo(() => {
    const grouped: Record<string, typeof habits> = {};
    
    todayHabits.forEach(habit => {
      // Suportar múltiplos pilares (pillars array) ou pilar único (pillar)
      const habitPillars = habit.pillars && habit.pillars.length > 0 
        ? habit.pillars 
        : habit.pillar ? [habit.pillar] : [];
      
      habitPillars.forEach(pillarSlug => {
        if (!grouped[pillarSlug]) grouped[pillarSlug] = [];
        // Evitar duplicatas
        if (!grouped[pillarSlug].some(h => h.id === habit.id)) {
          grouped[pillarSlug].push(habit);
        }
      });
    });
    
    return grouped;
  }, [todayHabits]);

  const radarChartData = useMemo(() => {
    if (!user || historicalScores.length === 0) return [];
    
    const currentWeekStart = getMonday(new Date(today));
    const currentWeekStartStr = currentWeekStart.toISOString().split('T')[0];
    
    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekStartStr = lastWeekStart.toISOString().split('T')[0];
    
    return user.pilares_foco.map(pillar => {
      const pillarScores = historicalScores
        .filter(s => s.pillar === pillar)
        .sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime());
      
      const currentScore = pillarScores.find(s => s.weekStart === currentWeekStartStr);
      const lastScore = pillarScores.find(s => s.weekStart === lastWeekStartStr);
      
      return { 
        pillar, 
        currentWeek: currentScore?.score || 0, 
        lastWeek: lastScore?.score 
      };
    });
  }, [user, historicalScores, today]);
  
  if (!user) return null;

  return (
    <MainLayout>
      {showTour && <OnboardingTour />}
      <div className="min-h-screen bg-muted/30">
        <div className="container max-w-6xl py-8 px-4 space-y-6">
          <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0ms' }}>
            <h1 className="text-3xl font-bold">Olá, {user.nome.split(' ')[0]}! 👋</h1>
            <p className="text-muted-foreground">{formatDateFriendly(today, userTimezone)}</p>
          </div>

          <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
            <StimulusCard />
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '150ms' }}>
            <MicroCoach />
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
            <IdentityVoteDisplay />
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '250ms' }}>
            <GroupsHomeSection />
          </div>

          <Card 
            data-tour="daily-progress"
            className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 animate-fade-in hover-scale" 
            style={{ animationDelay: '300ms' }}
            onClick={(e) => {
              setProgressSheetOpen(true);
              e.currentTarget.blur(); // Remove o foco após clicar
            }}
            tabIndex={0}
            role="button"
            aria-label="Ver progresso de hoje"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Progresso de hoje
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </CardTitle>
                <span className="text-3xl font-bold text-primary">{progressPercent}%</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={progressPercent} className="h-3" />
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {completedToday.length} de {todayHabits.length} hábitos completados
                </p>
                <Button variant="ghost" size="sm" className="text-xs">Ver detalhes →</Button>
              </div>
            </CardContent>
          </Card>

          <div ref={challengeSection.elementRef} data-tour="habits-section" className="animate-fade-in" style={{ animationDelay: '350ms' }}>
            {!challengeSection.isVisible ? <ChallengeSkeleton /> : (
              challenges.length === 0 ? (
                <Card className="border-none shadow-lg bg-gradient-to-br from-warning/20 via-warning/10 to-warning/5 cursor-pointer hover:shadow-xl transition-all hover-scale" onClick={() => navigate('/desafio')}>
                  <CardContent className="pt-8 pb-8 text-center space-y-4">
                    <div className="flex justify-center">
                      <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-warning/30">
                        <Flame className="h-10 w-10 text-warning animate-pulse" />
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Desafio 90 Dias</h2>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        Transforme sua vida com o poder da consistência.
                      </p>
                    </div>
                    <Button size="lg" className="bg-warning hover:bg-warning/90 text-white">
                      <Flame className="mr-2 h-5 w-5" />
                      Iniciar Meu Primeiro Desafio
                    </Button>
                  </CardContent>
                </Card>
              ) : (() => {
                const activeChallenge = challenges.find(c => !c.archived);
                return activeChallenge ? (
                  <Card className="border-none shadow-md bg-gradient-to-br from-warning/10 to-warning/5 hover-scale">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-warning/20">
                          <Flame className="h-6 w-6 text-warning" />
                        </div>
                        <div className="flex-1">
                          <CardTitle>Desafio 90 Dias</CardTitle>
                          <p className="text-sm text-muted-foreground">Sequência atual</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-warning">{activeChallenge.streak}</div>
                          <p className="text-xs text-muted-foreground">dias</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progresso</span>
                          <span className="font-medium">{activeChallenge.totalDays}/90 dias</span>
                        </div>
                        <Progress value={activeChallenge.totalDays / 90 * 100} className="h-2" />
                      </div>
                      <Button className="w-full mt-4" variant="outline" onClick={() => navigate(`/desafio/${activeChallenge.id}`)}>
                        Ver Desafio
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-none shadow-md bg-gradient-to-br from-warning/10 to-warning/5 cursor-pointer hover-scale" onClick={() => navigate('/desafio')}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-warning/20">
                          <Flame className="h-6 w-6 text-warning" />
                        </div>
                        <div className="flex-1">
                          <CardTitle>Desafio 90 Dias</CardTitle>
                          <p className="text-sm text-muted-foreground">Comece um novo desafio</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full" variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Iniciar Novo Desafio
                      </Button>
                    </CardContent>
                  </Card>
                );
              })()
            )}
          </div>

          <div ref={pillarsSection.elementRef} data-tour="pillars-section" className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            {!pillarsSection.isVisible ? <PillarsSkeleton /> : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Seus pilares</h2>
                  <Button variant="ghost" onClick={() => navigate('/pilares')}>Ver Todos</Button>
                </div>

                {radarChartData.length > 0 && (
                  <Card className="border-none shadow-md hover-scale">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <TrendingUp className="h-5 w-5 text-primary" />
                        </div>
                        Evolução Semanal dos Pilares
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PillarRadarChart scores={radarChartData} />
                    </CardContent>
                  </Card>
                )}

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {PILLAR_SLUGS.map((slug, index) => {
                    const pillar = PILLAR_INFO[slug];
                    const pillarHabits = habitsByPillar[slug] || [];
                    const completed = pillarHabits.filter(h => h.history.includes(today));
                    const progress = pillarHabits.length > 0 ? Math.round(completed.length / pillarHabits.length * 100) : 0;
                    const iconName = pillar.icon.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
                    const IconComponent = Icons[iconName as keyof typeof Icons] as any || Icons.Activity as any;

                    return (
                      <Card 
                        key={slug}
                        id={`pillar-card-${slug}`}
                        data-pillar-slug={slug}
                        className="cursor-pointer hover:shadow-lg transition-shadow animate-fade-in hover-scale" 
                        style={{ 
                          animationDelay: `${200 + (index * 50)}ms`,
                          pointerEvents: 'auto',
                          animationFillMode: 'forwards'
                        }}
                        onClick={() => navigate(`/pilar/${slug}`)}
                        aria-label={`Navegar para pilar ${pillar.name} com ${pillarHabits.length} ${pillarHabits.length === 1 ? 'hábito' : 'hábitos'} e ${progress}% de progresso hoje`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/pilar/${slug}`);
                          }
                        }}
                      >
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex items-start gap-3">
                              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: pillar.lightColor }}>
                                <IconComponent className="h-6 w-6" style={{ color: pillar.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold truncate">{pillar.name}</h3>
                                <p className="text-sm text-muted-foreground">
                                  {pillarHabits.length === 0 ? 'Sem hábitos' : `${pillarHabits.length} ${pillarHabits.length === 1 ? 'hábito' : 'hábitos'}`}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Hoje</span>
                                <span className="font-medium">{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-2" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 animate-fade-in hover-scale" style={{ animationDelay: '750ms' }}>
            <CardContent className="pt-6 text-center">
              <Quote className="w-8 h-8 text-primary mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">{dailyQuote.text}</p>
              <p className="text-sm text-muted-foreground">— {dailyQuote.author}</p>
            </CardContent>
          </Card>
        </div>
      </div>
      <TodayProgressSheet open={progressSheetOpen} onOpenChange={setProgressSheetOpen} />
    </MainLayout>
  );
}

function getMonday(date: Date): Date {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}
